/**
 * Comment pins (UX Pack F5b) — clickable badges on the glass overlay CONTENT
 * layer (translateY-synced with iframe scroll, zoom applied like the collab
 * cursors). Element pins position from `query-rects-for-eids` (the F4b
 * measurement channel — raw document-relative CSS px); section pins ride the
 * existing `iframe:section-positions` feed. Anchors resolve per CURRENT state
 * on every refresh: orphaned threads simply render no pin (the sidebar owns
 * the disclosed orphan group — a pin must never point at a guess).
 */

import { eventBus } from '../core/events.js';
import { store } from '../core/state.js';
import { bridge } from '../editing/bridge.js';
import {
  getCommentThreads,
  getThreadNumbers,
  getActiveCommentThread,
  activateThreadAndNavigate,
  resolveThreadAnchor,
  isCommentsDisabled
} from './comment-controller.js';

let _cfg = { getContentElement: () => null };
let _pins = new Map(); // threadId → element
let _sectionTops = new Map(); // instanceId → {top, height}
let _contentHeight = 0; // visual px — full scrollable page height
let _viewportHeight = 0; // visual px — the slice on screen
let _scrollY = 0; // visual px
let _installed = false;
let _measureTimer = null;
let _requestSeq = 0;
let _offs = [];

function layerEl() {
  const content = _cfg.getContentElement?.();
  if (!content) return null;
  let layer = content.querySelector(':scope > .pb-comment-pin-layer');
  if (!layer) {
    layer = document.createElement('div');
    layer.className = 'pb-comment-pin-layer';
    content.appendChild(layer);
  }
  return layer;
}

function pinFor(thread, layer) {
  let pin = _pins.get(thread.id);
  if (pin) return pin;
  // pb-sweep-exempt: no-hand-rolled-button — a canvas pin badge is positioned chrome (transform-placed, count-labeled), not a kit action button; it carries its own aria-label and has no busy/async lifecycle
  pin = document.createElement('button');
  pin.type = 'button';
  pin.className = 'pb-comment-pin';
  pin.dataset.threadId = thread.id;
  pin.addEventListener('click', () => {
    // Same route as a sidebar card: a pin is always on the active page, but
    // going through the shared helper keeps activation semantics identical.
    void activateThreadAndNavigate(thread);
    eventBus.emit('comments:open-thread', { threadId: thread.id });
  });
  layer.appendChild(pin);
  _pins.set(thread.id, pin);
  return pin;
}

/**
 * Place one pin on the overlay's content layer, which is sized and translated
 * in VISUAL (zoom-multiplied) px.
 *
 * The two anchor kinds arrive in DIFFERENT coordinate spaces, and conflating
 * them double-scaled every section pin at any zoom other than 1:
 *   • section tops come from `iframe:section-positions`, which the bridge
 *     ALREADY divides by zoom (bridge.js `_normalizeSectionPositions`) → these
 *     are visual px, pass `coordsAreVisual: true`.
 *   • element rects come from `rects-for-eids-result`, which the bridge is
 *     contractually forbidden to touch (bridge-compat-tests) → raw iframe px,
 *     pass `coordsAreVisual: false` so they get scaled here.
 */
function placePin(pin, thread, number, x, y, zoom, { coordsAreVisual = false } = {}) {
  const scale = coordsAreVisual ? 1 : zoom;
  pin.style.transform = `translate(${x * scale}px, ${y * scale}px)`;
  // The label is the thread's stable SEQUENCE number (matches the sidebar
  // card avatar, F5c) — the message count lives in the aria-label.
  pin.textContent = String(number || '');
  pin.dataset.tooltip =
    `${thread.comments?.[0]?.authorName || 'Comment'}: ${thread.comments?.[0]?.body || ''}`.slice(
      0,
      120
    );
  const messages = thread.comments?.length || 1;
  pin.setAttribute(
    'aria-label',
    `Comment ${number} — ${thread.status === 'resolved' ? 'resolved' : 'open'} (${messages} message${messages === 1 ? '' : 's'})`
  );
  pin.classList.toggle('is-resolved', thread.status === 'resolved');
  pin.classList.toggle('is-active', thread.id === getActiveCommentThread());
  pin.hidden = false;
}

/** Re-measure + re-place every pin for the ACTIVE page. Debounced. */
function scheduleRefresh() {
  clearTimeout(_measureTimer);
  _measureTimer = setTimeout(refreshPins, 250);
}

async function refreshPins() {
  const layer = layerEl();
  if (!layer) return;
  if (isCommentsDisabled()) {
    for (const pin of _pins.values()) pin.remove();
    _pins.clear();
    return;
  }
  const activePage = store.getState()?.activePage || null;
  const threads = getCommentThreads().filter((t) => t.pageId === activePage);
  const numbers = getThreadNumbers();
  const seen = new Set();
  const zoom = bridge.getZoom ? bridge.getZoom() : 1;
  // Every y a pin lands on, republished for the rail and the minimap. They
  // must not re-measure: a second rect round-trip would be both wasteful and
  // free to disagree with the pins, which is the whole reason thread NUMBERS
  // are computed in one place too.
  const placed = [];

  // Partition by resolution kind; element pins group per section for the
  // rect query, orphans get NO pin by contract.
  const bySection = new Map();
  for (const thread of threads) {
    const resolution = resolveThreadAnchor(thread);
    if (resolution.kind === 'section') {
      const sectionPos = _sectionTops.get(thread.instanceId);
      if (sectionPos) {
        // Section tops are bridge-normalized → already visual px.
        placePin(
          pinFor(thread, layer),
          thread,
          numbers.get(thread.id),
          12,
          sectionPos.top + 12,
          zoom,
          { coordsAreVisual: true }
        );
        seen.add(thread.id);
        placed.push({ threadId: thread.id, y: sectionPos.top + 12, status: thread.status });
      }
    } else if (resolution.kind === 'element') {
      if (!bySection.has(thread.instanceId)) bySection.set(thread.instanceId, []);
      bySection.get(thread.instanceId).push({ thread, eid: resolution.eid });
    }
  }

  await Promise.all(
    Array.from(bySection.entries()).map(([instanceId, entries]) => {
      const requestId = `comment-pins-${++_requestSeq}`;
      return new Promise((resolve) => {
        const timer = setTimeout(() => {
          off();
          resolve();
        }, 1500);
        const off = eventBus.on('iframe:rects-for-eids-result', (data) => {
          if (data?.requestId !== requestId) return;
          clearTimeout(timer);
          off();
          const rectByEid = new Map((data.items || []).map((item) => [item.eid, item.rect]));
          for (const { thread, eid } of entries) {
            const rect = rectByEid.get(eid);
            if (!rect) continue; // unmeasurable → no pin, sidebar discloses
            // Element rects are raw iframe px by bridge contract → scale here.
            placePin(
              pinFor(thread, layer),
              thread,
              numbers.get(thread.id),
              rect.left + rect.width - 8,
              rect.top - 8,
              zoom,
              { coordsAreVisual: false }
            );
            seen.add(thread.id);
            placed.push({ threadId: thread.id, y: (rect.top - 8) * zoom, status: thread.status });
          }
          resolve();
        });
        bridge.send('query-rects-for-eids', {
          instanceId,
          eids: entries.map((entry) => entry.eid),
          requestId
        });
      });
    })
  );

  // Drop pins for threads that vanished, changed page, or went orphaned.
  for (const [threadId, pin] of _pins) {
    if (!seen.has(threadId)) {
      pin.remove();
      _pins.delete(threadId);
    }
  }

  // All coordinates below are VISUAL px, the space the overlay content layer
  // lives in — so a consumer can turn one into a percentage of the page with
  // `y / contentHeight` and nothing else.
  eventBus.emit('comments:pin-positions', {
    positions: placed.sort((a, b) => a.y - b.y),
    contentHeight: _contentHeight,
    viewportHeight: _viewportHeight,
    scrollY: _scrollY
  });
}

export function initCommentPins({ getContentElement } = {}) {
  if (_installed) return () => {};
  _installed = true;
  _cfg = { getContentElement };
  _offs = [
    eventBus.on('comments:changed', scheduleRefresh),
    eventBus.on('comments:active-thread', ({ threadId } = {}) => {
      // No id interpolation into selectors — compare dataset values instead.
      for (const [id, pin] of _pins) pin.classList.toggle('is-active', id === threadId);
    }),
    eventBus.on('iframe:section-positions', (data) => {
      _sectionTops = new Map(
        (Array.isArray(data?.positions) ? data.positions : []).map((s) => [
          s.instanceId,
          { top: s.top, height: s.height }
        ])
      );
      // Already bridge-scaled to visual px, same as the tops above.
      _contentHeight = Number(data?.contentHeight) || 0;
      _viewportHeight = Number(data?.viewportHeight) || 0;
      _scrollY = Number(data?.scrollY) || 0;
      scheduleRefresh();
    }),
    eventBus.on('iframe:bridge-ready', scheduleRefresh)
  ];
  return () => {
    clearTimeout(_measureTimer);
    for (const off of _offs) if (typeof off === 'function') off();
    _offs = [];
    for (const pin of _pins.values()) pin.remove();
    _pins.clear();
    _installed = false;
  };
}
